/**
 * Budget spreadsheet-import helpers (v2).
 *
 * Everything is CLIENT-SIDE (SheetJS): parse the workbook / pasted text into
 * string rows, guess a column mapping from the header row, validate each row
 * and shape it into the SDK's ImportBudgetItemRow. The server never sees the
 * file — POST api/budgets/{id}/items/import receives the mapped rows and is
 * strict all-or-nothing (any invalid row fails the import with rowErrors).
 *
 * Mirrors the mapping idiom of admin/Communications/_shared/BulkSendWizard and
 * the preview/validation idiom of app/AssetManagement/BulkImport.
 */

import * as XLSX from 'xlsx';
import type { ImportBudgetItemRow } from '@actschurch/ccms-sdk';

/** Select value for "this field has no source column". */
export const NOT_MAPPED = '__not_mapped__';

/** Column mapping: import field -> sheet column index (as string) or NOT_MAPPED. */
export interface ImportColumnMapping {
  category: string;
  itemDescription: string;
  quantity: string;
  unitPrice: string;
  location: string;
  notes: string;
}

export const EMPTY_MAPPING: ImportColumnMapping = {
  category: NOT_MAPPED,
  itemDescription: NOT_MAPPED,
  quantity: NOT_MAPPED,
  unitPrice: NOT_MAPPED,
  location: NOT_MAPPED,
  notes: NOT_MAPPED,
};

export interface MappingFieldDef {
  field: keyof ImportColumnMapping;
  label: string;
  required?: boolean;
  /** Normalized header names that auto-map to this field. */
  aliases: string[];
}

export const MAPPING_FIELDS: MappingFieldDef[] = [
  { field: 'category', label: 'Category', aliases: ['category', 'section', 'group'] },
  {
    field: 'itemDescription',
    label: 'Item description',
    required: true,
    aliases: ['item description', 'description', 'item', 'name', 'details'],
  },
  { field: 'quantity', label: 'Qty', aliases: ['qty', 'quantity', 'count', 'units'] },
  {
    field: 'unitPrice',
    label: 'Unit price',
    aliases: ['unit price', 'unitprice', 'price', 'unit cost', 'cost', 'rate', 'amount'],
  },
  { field: 'location', label: 'Location', aliases: ['location', 'campus', 'site', 'branch'] },
  { field: 'notes', label: 'Notes', aliases: ['notes', 'note', 'comments', 'comment', 'remarks'] },
];

const normalize = (s: string) => s.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

/** Parse a CSV/XLSX/XLS file into an array-of-arrays of trimmed strings. */
export async function parseSpreadsheetFile(file: File): Promise<string[][]> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, blankrows: false, defval: '' }) as string[][];
  return rows.map((r) => r.map((c) => String(c ?? '').trim()));
}

/**
 * Parse pasted text into rows. Excel copy-paste is tab-separated; otherwise
 * fall back to comma-separated. Rows split on newlines.
 */
export function parsePastedRows(text: string): string[][] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const sep = lines.some((l) => l.includes('\t')) ? '\t' : ',';
  return lines.map((l) => l.split(sep).map((c) => c.trim()));
}

/** True when the row looks like a header (any cell matches a known alias). */
export function looksLikeHeaderRow(row: string[] | undefined): boolean {
  if (!row) return false;
  return row.some((cell) => {
    const n = normalize(String(cell || ''));
    return !!n && MAPPING_FIELDS.some((f) => f.aliases.includes(n));
  });
}

/** Auto-map columns from a header row (exact alias match first, then contains). */
export function guessMapping(headerRow: string[]): ImportColumnMapping {
  const mapping: ImportColumnMapping = { ...EMPTY_MAPPING };
  const taken = new Set<number>();
  const headers = headerRow.map((h) => normalize(String(h || '')));

  // Exact alias matches win, in field order.
  for (const def of MAPPING_FIELDS) {
    const idx = headers.findIndex((h, i) => !taken.has(i) && !!h && def.aliases.includes(h));
    if (idx >= 0) {
      mapping[def.field] = String(idx);
      taken.add(idx);
    }
  }
  // Then loose "contains" matches for anything still unmapped.
  for (const def of MAPPING_FIELDS) {
    if (mapping[def.field] !== NOT_MAPPED) continue;
    const idx = headers.findIndex(
      (h, i) => !taken.has(i) && !!h && def.aliases.some((a) => h.includes(a)),
    );
    if (idx >= 0) {
      mapping[def.field] = String(idx);
      taken.add(idx);
    }
  }
  return mapping;
}

/** "1 234,56" / "R1,234.56" / "3500" -> number, or undefined when blank/invalid. */
export function coerceNumber(raw: string): number | undefined {
  const cleaned = (raw || '').replace(/[^\d.,-]/g, '').replace(/,/g, '');
  if (!cleaned) return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

/** One preview row: the mapped SDK row plus client-side validation errors. */
export interface ParsedImportRow {
  /** Original sheet row number (1-based, header included) for error reporting. */
  rowNumber: number;
  row: ImportBudgetItemRow;
  errors: string[];
}

/**
 * Map + validate data rows (header already stripped). `firstRowNumber` is the
 * sheet row number of the first data row (2 when a header row is present).
 * Fully-empty rows (across mapped columns) are skipped.
 */
export function buildImportRows(
  dataRows: string[][],
  mapping: ImportColumnMapping,
  firstRowNumber: number,
): ParsedImportRow[] {
  const pick = (r: string[], field: keyof ImportColumnMapping): string => {
    const col = mapping[field];
    if (col === NOT_MAPPED) return '';
    return String(r[Number(col)] ?? '').trim();
  };

  const parsed: ParsedImportRow[] = [];
  dataRows.forEach((r, i) => {
    const rowNumber = firstRowNumber + i;
    const category = pick(r, 'category');
    const itemDescription = pick(r, 'itemDescription');
    const quantityRaw = pick(r, 'quantity');
    const unitPriceRaw = pick(r, 'unitPrice');
    const location = pick(r, 'location');
    const notes = pick(r, 'notes');

    // Skip rows that are empty across every mapped column.
    if (!category && !itemDescription && !quantityRaw && !unitPriceRaw && !location && !notes) return;

    const errors: string[] = [];
    if (!itemDescription) errors.push('Item description is required');

    const quantity = coerceNumber(quantityRaw);
    if (quantityRaw && quantity === undefined) errors.push(`Qty "${quantityRaw}" is not a number`);
    if (quantity !== undefined && quantity < 0) errors.push('Qty may not be negative');

    const unitPrice = coerceNumber(unitPriceRaw);
    if (unitPriceRaw && unitPrice === undefined) errors.push(`Unit price "${unitPriceRaw}" is not a number`);
    if (unitPrice !== undefined && unitPrice < 0) errors.push('Unit price may not be negative');

    parsed.push({
      rowNumber,
      errors,
      row: {
        rowNumber,
        category: category || undefined,
        itemDescription,
        quantity,
        unitPrice,
        location: location || undefined,
        notes: notes || undefined,
      },
    });
  });
  return parsed;
}

/** Download a sample workbook whose headers auto-map (same idiom as BulkSendWizard). */
export function downloadSampleWorkbook(ext: 'csv' | 'xlsx' = 'xlsx'): void {
  const headers = ['Category', 'Item description', 'Qty', 'Unit price', 'Location', 'Notes'];
  const rows = [
    ['Sound', 'Wireless microphone', '2', '3500', '', 'Replaces broken units'],
    ['Sound', 'XLR cables (10m)', '10', '150', '', ''],
    ['Catering', 'Volunteer lunches', '120', '85', '', 'Per event'],
    ['', 'Contingency', '1', '5000', '', 'No category = uncategorized'],
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws['!cols'] = headers.map((h) => ({ wch: Math.max(h.length, 16) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Budget items');
  XLSX.writeFile(wb, `budget-items-import-sample.${ext}`);
}
